/**
 * SnoozePopover — preset-based snooze picker for inbox rows + bulk
 * actions, modelled on Superhuman / Hey.
 *
 * Layout
 * ------
 *   ┌──────────────────────────────────┐
 *   │  Snooze fino a…                  │
 *   │  • Tra 1 ora                     │
 *   │  • Stasera (18:00)               │
 *   │  • Domani (9:00)                 │
 *   │  • Lunedì (9:00)                 │
 *   │  • Prossima settimana            │
 *   └──────────────────────────────────┘
 *
 * The presets come from :data:`DEFAULT_SNOOZE_PRESETS` so the calendar
 * vocabulary lives in one place. Each preset resolves to an
 * ``epoch-ms`` target on click; the parent decides whether to snooze a
 * single message or a bulk set via :prop:`onPick`.
 *
 * Accessibility
 * -------------
 *  - Renders as a Radix Popover (focus trap + escape close + portal).
 *  - Trigger is supplied by the caller via :prop:`children` (Radix
 *    ``asChild``) so the call-site keeps full styling control.
 *  - Each preset is a real ``<button>`` — keyboard navigable via Tab,
 *    Enter activates.
 *
 * A "Personalizzato…" entry is intentionally NOT shipped in this
 * iteration: the BE field is FE-only today (see :file:`useSnoozeStore.ts`)
 * so a date picker would over-promise. Re-introduce when the BE column
 * lands.
 */

import { Clock } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { useLingui } from "@lingui/react/macro";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@ando/ui";

import {
  DEFAULT_SNOOZE_PRESETS,
  type SnoozePreset,
} from "../hooks/useSnoozeStore";


export interface SnoozePopoverProps {
  /** Trigger element — rendered via Radix ``asChild``. */
  readonly children: React.ReactElement;
  /** Resolve the picked epoch-ms (already computed from "now"). */
  readonly onPick: (epochMs: number) => void;
  /** Override the default Italian preset set. */
  readonly presets?: ReadonlyArray<SnoozePreset>;
  /** Override the popover label — defaults to "Snooze fino a…". */
  readonly label?: string;
  /** Class merge for the content container. */
  readonly className?: string;
}


export const SnoozePopover: React.FC<SnoozePopoverProps> = ({
  children,
  onPick,
  presets = DEFAULT_SNOOZE_PRESETS,
  label,
  className,
}) => {
  const { t, i18n } = useLingui();
  const resolvedLabel = label ?? t`Snooze fino a…`;
  const [open, setOpen] = useState(false);
  const handlePick = (preset: SnoozePreset) => {
    // Resolve from a fresh ``new Date()`` so the preset rolls correctly
    // (e.g. "Stasera" landing on the right day).
    onPick(preset.resolve(new Date()));
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className={cn("w-56 p-1.5", className)}
      >
        <div className="flex items-center gap-1.5 px-2 pt-1 pb-1.5 font-medium text-[10.5px] text-ink-3 uppercase tracking-[0.05em]">
          <Clock size={11} strokeWidth={2} aria-hidden />
          {resolvedLabel}
        </div>
        <ul className="flex flex-col gap-0.5">
          {presets.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => handlePick(p)}
                className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left font-medium text-[12px] text-ink-1 transition-colors hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {i18n._(p.label)}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
SnoozePopover.displayName = "SnoozePopover";
